import { memo, useCallback, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useLocation } from 'react-router-dom';
import {
  EventAccordionItemProps,
  InventoryEventsAccordion,
  translateEventToInventoryAccordionItem,
} from 'src/components/Accordions';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { MultiSelectActionBar } from 'src/components/common/MultiSelect/MultiSelectActionBar';
import { SetInventoryEventAccordionSelectionCountLabel } from 'src/components/Events/EventListing/InventoryEventListing/SetInventoryEventAccordionSelectionCountLabel';
import { SetInventoryMetricsSelectionCountLabel } from 'src/components/Events/EventListing/InventoryEventListing/SetInventoryMetricsViewSelectionCountLabel';
import { useTrackComponent } from 'src/contexts/AppPerformanceContext';
import {
  useCatalogDataContextErrorInfoSelector,
  useCatalogDataContextEventsExpansionSelector,
  useCatalogDataContextEventsSelector,
  useCatalogDataContextIsLoadingSelector,
} from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { EventHubContextProvider } from 'src/contexts/EventHubContext';
import { useFilterQueryContextFilterQuerySelector } from 'src/contexts/FilterQueryContext/FilterQueryContext.selectors';
import { NO_GROUP_ID } from 'src/contexts/MultiSelectionContext';
import {
  useMultiSelectionContextAllGroupsIdsSelector,
  useMultiSelectionContextClearShiftKeySelectionSelector,
  useMultiSelectionContextSetSelectionModeSelector,
} from 'src/contexts/MultiSelectionContext/MultiSelectionContext.selectors';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useScrollToEventIndexFromBackSection } from 'src/hooks/useScrollToEventIndexFromBackSection';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { SomethingWentWrong } from 'src/views';
import { InventoryViewMode, Listing, ListingQuery } from 'src/WebApiController';

import * as styles from '../EventListing.css';
import { InventoryGlobalBulkActions } from './BulkActions';
import { InventoryDetailedEventView } from './InventoryDetailedEventView';
import { InventoryEventSummaryMetrics } from './InventoryEventSummaryMetrics';
import { InventoryFlattenedView } from './InventoryFlattenedView';

function InventoryEventListingRaw({
  onItemsRendered,
}: {
  onItemsRendered?: (items: EventAccordionItemProps<Listing>[]) => void;
}) {
  useTrackComponent('InventoryEventListing');
  const isLoading = useCatalogDataContextIsLoadingSelector();
  const errorInfo = useCatalogDataContextErrorInfoSelector();
  const eventsTransformed = useCatalogDataContextEventsSelector();
  const { expandedListItems, toggleListItemExpansion } =
    useCatalogDataContextEventsExpansionSelector();
  const [filterQuery] =
    useFilterQueryContextFilterQuerySelector<ListingQuery>();
  const location = useLocation();

  const hasConsignorMasterUI = useUserHasConsignorMasterUI();

  const onInventoryEventExpanded = useCallback(
    async (viagVirtId: string) => {
      toggleListItemExpansion([viagVirtId]);
    },
    [toggleListItemExpansion]
  );

  const { returningFromViagVirtId, scrollToItemIndex } =
    useScrollToEventIndexFromBackSection();

  const eventIdFromHash =
    location.hash.length === 37 ? location.hash.substring(1) : undefined; // UUID 36 chars

  useEffect(() => {
    if (eventIdFromHash) {
      toggleListItemExpansion([eventIdFromHash]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIdFromHash]);

  const [allGroupsIds, setAllGroupsIds] =
    useMultiSelectionContextAllGroupsIdsSelector();
  const clearShiftKeySelection =
    useMultiSelectionContextClearShiftKeySelectionSelector();
  const setSelectionMode = useMultiSelectionContextSetSelectionModeSelector();

  // Reset and close selection mode when changing view
  useEffect(() => {
    clearShiftKeySelection();
    setSelectionMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery.viewMode]);

  const listingItems = useMemo(() => {
    return (
      eventsTransformed?.map((e, i) =>
        translateEventToInventoryAccordionItem(e, i)
      ) || []
    );
  }, [eventsTransformed]);

  useEffect(() => {
    // Important useEffect to keep allGroupIds in sync with the items
    // This is important for the multi select to work properly
    if (filterQuery.viewMode === InventoryViewMode.FlattenedView) {
      if (!isEqual(allGroupsIds, [NO_GROUP_ID])) {
        setAllGroupsIds([NO_GROUP_ID]);
      }
    } else {
      const groupIds = listingItems.map((listingItem) => listingItem.itemId);
      if (!isEqual(allGroupsIds, groupIds)) {
        setAllGroupsIds(groupIds);
      }
    }
  }, [listingItems, filterQuery.viewMode, allGroupsIds, setAllGroupsIds]);

  return (
    <EventHubContextProvider>
      {isLoading && <PosSpinner />}
      {errorInfo && (
        <SomethingWentWrong
          header={errorInfo.errorHeader}
          message={<Content id={ContentId.FailToLoadListContent} />}
        />
      )}
      {!isLoading &&
        !errorInfo &&
        (eventsTransformed && eventsTransformed.length > 0 ? (
          <div className={styles.eventsListContainer}>
            {filterQuery.viewMode === InventoryViewMode.MetricView ? (
              <InventoryDetailedEventView
                before={
                  !hasConsignorMasterUI && <InventoryEventSummaryMetrics />
                }
                topListItemBefore={
                  <SetInventoryMetricsSelectionCountLabel
                    events={eventsTransformed}
                  >
                    <MultiSelectActionBar>
                      <InventoryGlobalBulkActions />
                    </MultiSelectActionBar>
                  </SetInventoryMetricsSelectionCountLabel>
                }
                items={eventsTransformed}
                highlightEvent={eventIdFromHash}
                scrollToEventVirtId={returningFromViagVirtId}
              />
            ) : filterQuery.viewMode === InventoryViewMode.FlattenedView ? (
              <InventoryFlattenedView
                before={
                  !hasConsignorMasterUI && <InventoryEventSummaryMetrics />
                }
                enableColumnFilters
              />
            ) : (
              <InventoryEventsAccordion
                before={
                  !hasConsignorMasterUI && <InventoryEventSummaryMetrics />
                }
                topListItemBefore={
                  <SetInventoryEventAccordionSelectionCountLabel>
                    <MultiSelectActionBar>
                      <InventoryGlobalBulkActions />
                    </MultiSelectActionBar>
                  </SetInventoryEventAccordionSelectionCountLabel>
                }
                items={listingItems}
                disablePagination
                scrollToItemIndex={scrollToItemIndex}
                selectedIds={expandedListItems}
                onSelect={onInventoryEventExpanded}
                onItemsRendered={onItemsRendered}
              />
            )}
          </div>
        ) : (
          <EmptySectionContent.Root
            icon={
              <EmptySectionContent.SolidIconContainer>
                <SearchSolidIcon />
              </EmptySectionContent.SolidIconContainer>
            }
          >
            <EmptySectionContent.Label>
              <Content id={ContentId.NoResultFound} />
            </EmptySectionContent.Label>
            <EmptySectionContent.DetailMessage>
              <Content id={ContentId.NoEventsMatching} />
            </EmptySectionContent.DetailMessage>
          </EmptySectionContent.Root>
        ))}
    </EventHubContextProvider>
  );
}

export const InventoryEventListing = memo(InventoryEventListingRaw);
